import { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Save, CheckCircle, AlertTriangle, Code, Type } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGES = [
  { key: 'home', label: 'Homepage' },
  { key: 'about', label: 'About' },
  { key: 'services', label: 'Services' },
  { key: 'government', label: 'Government' },
  { key: 'contact', label: 'Contact' },
  { key: 'careers', label: 'Careers' },
];

function StringField({ label, value, onChange, multiline }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#888] font-bold mb-1.5 flex items-center gap-2">
        <Type size={11} className="text-[#555]" /> {label}
      </label>
      {multiline ? (
        <Textarea
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          data-testid={`content-field-${label.replace(/\s+/g, '-')}`}
          className="bg-transparent border-white/10 text-white text-sm focus:border-[#F2B705] resize-none"
        />
      ) : (
        <Input
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`content-field-${label.replace(/\s+/g, '-')}`}
          className="bg-transparent border-white/10 text-white text-sm h-10 focus:border-[#F2B705]"
        />
      )}
    </div>
  );
}

function JsonField({ label, value, onChange }) {
  const [raw, setRaw] = useState(() => JSON.stringify(value ?? [], null, 2));
  const [error, setError] = useState('');

  // Re-sync local raw if external value reference changes (e.g. on page switch)
  useEffect(() => {
    setRaw(JSON.stringify(value ?? [], null, 2));
    setError('');
  }, [value]);

  const handleChange = (e) => {
    const text = e.target.value;
    setRaw(text);
    try {
      const parsed = JSON.parse(text);
      setError('');
      onChange(parsed);
    } catch (err) {
      setError('Invalid JSON — fix before saving');
    }
  };

  const itemsCount = Array.isArray(value) ? value.length : Object.keys(value || {}).length;

  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#888] font-bold mb-1.5 flex items-center gap-2">
        <Code size={11} className="text-[#F2B705]" /> {label}
        <span className="text-[10px] text-[#555] normal-case tracking-normal">
          ({Array.isArray(value) ? `${itemsCount} items` : 'object'} — JSON)
        </span>
      </label>
      <Textarea
        value={raw}
        onChange={handleChange}
        rows={Math.min(20, Math.max(4, raw.split('\n').length))}
        data-testid={`content-json-${label.replace(/\s+/g, '-')}`}
        className={`bg-[#0a0a0a] border-white/10 text-white text-xs font-mono focus:border-[#F2B705] resize-y ${error ? 'border-red-500/60' : ''}`}
        spellCheck="false"
      />
      {error && (
        <p className="text-xs text-red-400 mt-1 flex items-center gap-1.5">
          <AlertTriangle size={11} /> {error}
        </p>
      )}
    </div>
  );
}

export default function ContentEditor() {
  const [activePage, setActivePage] = useState('home');
  const [sections, setSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchContent = useCallback(async (page) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/admin/content/${page}`, { withCredentials: true });
      setSections(res.data.sections || {});
    } catch {
      setSections({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContent(activePage);
    setSaved(false);
    setError('');
  }, [activePage, fetchContent]);

  const updateField = (key, val) => {
    setSections((prev) => ({ ...prev, [key]: val }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await axios.put(`${API}/api/admin/content/${activePage}`, { sections }, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setSaving(false);
    }
  };

  const sectionKeys = useMemo(() => Object.keys(sections), [sections]);

  const isMultilineString = (key) =>
    key.includes('description') ||
    key.includes('_p') ||
    key.includes('mission') ||
    key.includes('vision') ||
    key.includes('intro') ||
    key.includes('text') ||
    key.includes('story');

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Page Content</h1>
          <p className="text-xs text-[#666] mt-1">
            Strings are edited inline. Lists/objects (services, NAICS, testimonials, etc.) are edited as JSON.
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="content-save-btn"
          className="bg-[#F2B705] text-black font-bold text-xs uppercase tracking-wide px-5 py-2.5 hover:bg-[#D9A404] transition-colors flex items-center gap-2 disabled:opacity-50"
        >
          {saved ? (
            <><CheckCircle size={14} /> Saved</>
          ) : (
            <><Save size={14} /> {saving ? 'Saving...' : 'Save Changes'}</>
          )}
        </button>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 text-red-400 text-sm p-3 mb-4 flex items-center gap-2">
          <AlertTriangle size={14} /> {error}
        </div>
      )}

      {/* Page tabs */}
      <div className="flex gap-1 mb-8 overflow-x-auto pb-2">
        {PAGES.map((p) => (
          <button
            key={p.key}
            onClick={() => setActivePage(p.key)}
            data-testid={`content-tab-${p.key}`}
            className={`px-4 py-2 text-xs uppercase tracking-wide font-bold transition-colors whitespace-nowrap ${
              activePage === p.key
                ? 'bg-[#F2B705] text-black'
                : 'bg-[#161616] text-[#888] hover:text-white border border-white/10'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Fields */}
      <div className="bg-[#161616] border border-white/10 p-6 space-y-5">
        {loading ? (
          <p className="text-[#666] text-sm">Loading…</p>
        ) : sectionKeys.length === 0 ? (
          <p className="text-[#666] text-sm">
            No content fields found for this page. Trigger seed (restart backend) to populate.
          </p>
        ) : (
          sectionKeys.map((key) => {
            const val = sections[key];
            const isComplex = typeof val === 'object' && val !== null;
            const label = key.replace(/_/g, ' ');
            if (isComplex) {
              return <JsonField key={key} label={label} value={val} onChange={(v) => updateField(key, v)} />;
            }
            return (
              <StringField
                key={key}
                label={label}
                value={val}
                onChange={(v) => updateField(key, v)}
                multiline={isMultilineString(key)}
              />
            );
          })
        )}
      </div>
    </div>
  );
}
